/**
 * ShipmentTrackingSection — OwnTune-branded shipment tracking for the
 * customer's Order Detail page.
 *
 * Reads `order.shipment_tracking[]` (built server-side by
 * `_build_customer_shipment_tracking`). Deliberately excludes
 * supplier-sensitive data — no origin address, no route/map, no
 * weight/volume, no external carrier link. Carrier name shown discreetly.
 *
 * Renders one card per destination that carries carrier tracking. On
 * a single-shipment order that's one card; on split orders each
 * destination gets its own card + label. A 5-step milestone tracker
 * marks the current normalized shipment state (DSV-inspired), and the
 * effective delivery date sits alongside the latest meaningful update.
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
    Truck, Check, AlertTriangle, MapPin, PackageCheck,
    ClipboardCheck, Warehouse,
} from 'lucide-react';
import { formatDate, formatDateTime } from '../lib/utils';
import { useTranslation } from '../contexts/TranslationContext';

// Distinct outline icon per milestone, plus the (i18n) label. Kept
// as-close-as-lucide-goes to what a DSV-style tracker uses:
//   Booked → clipboard-with-check (booking document)
//   In transit → truck (long-haul on the road)
//   At dispatching center → warehouse (building)
//   Out for delivery → map-pin (approaching final address, last mile)
//   Delivered → package-with-check (parcel handed over)
const STEPS = [
    { key: 'booked',              Icon: ClipboardCheck, labelKey: 'shipment.step.booked',            fallback: 'Booked' },
    { key: 'in_transportation',   Icon: Truck,          labelKey: 'shipment.step.in_transit',        fallback: 'In transit' },
    { key: 'at_dispatching_center', Icon: Warehouse,    labelKey: 'shipment.step.at_center',         fallback: 'At dispatching center' },
    { key: 'out_for_delivery',    Icon: MapPin,         labelKey: 'shipment.step.out_for_delivery',  fallback: 'Out for delivery' },
    { key: 'delivered',           Icon: PackageCheck,   labelKey: 'shipment.step.delivered',         fallback: 'Delivered' },
];

/**
 * Extract the LOCAL YYYY-MM-DD date part from an ISO timestamp.
 *
 * The server stores event timestamps in UTC, but the customer-facing
 * "Expected" date must reflect their LOCAL date — otherwise a shipment
 * that moved to out-for-delivery at 23:30 UTC on Oct 1 would show
 * "Expected Oct 2" to a user in UTC+1, even though for them it's
 * literally already-out-for-delivery-today. Returns ``null`` if the
 * input can't be parsed. Pure (SSR-safe).
 */
const localDateOf = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    // toLocaleDateString with en-CA returns a canonical YYYY-MM-DD in
    // the browser's local timezone — exactly what the rest of the
    // formatDate pipeline expects.
    return d.toLocaleDateString('en-CA');
};

const stepIndex = (status) => Math.max(0, STEPS.findIndex((s) => s.key === status));

/**
 * DSV-inspired 5-stage milestone tracker.
 *
 * Uses distinct outline icons per stage rather than plain dots so a
 * customer can scan the shipment state at a glance. State palette:
 *   - Completed   → emerald ring + emerald icon (with a small check badge)
 *   - Current     → OwnTune indigo, largest, ring-glow + label bolded
 *   - Future      → muted slate
 * Connecting bars between icons follow the same completed / future
 * split — a filled emerald bar leads INTO the current step; the bars
 * beyond the current step stay muted grey.
 *
 * Mobile (<sm): the row flex-wraps to two lines but keeps the same
 * icon hierarchy — a stacked column would break the DSV "milestone
 * ribbon" feel, so instead we keep icons in-row and let the labels
 * wrap under each. The container is horizontally scrollable as a
 * last-resort escape hatch on very narrow screens (<340px).
 */
const MilestoneTracker = ({ status }) => {
    const { t } = useTranslation();
    const cur = stepIndex(status);
    return (
        <div className="mt-6 mb-2" data-testid="shipment-tracking-progress">
            <ol className="flex items-start w-full">
                {STEPS.map((s, i) => {
                    const done = i < cur;
                    const active = i === cur;
                    // Minimalist palette:
                    //   done   → dark neutral icon, no circle, tiny green ✓ badge
                    //   active → prominent OwnTune-indigo filled circle, white icon
                    //   future → light grey icon, no circle
                    const iconWrap = active
                        ? 'bg-indigo-600 text-white shadow-md shadow-indigo-200 ring-4 ring-indigo-100'
                        : 'bg-transparent';
                    const iconColor = done
                        ? 'text-slate-700'
                        : active
                            ? 'text-white'
                            : 'text-slate-300';
                    const label = done
                        ? 'text-slate-700'
                        : active
                            ? 'text-indigo-700 font-semibold'
                            : 'text-slate-400';
                    // Thin connector lines — coloured through and INTO
                    // the current step (progress trail), muted grey
                    // for every segment after the current step.
                    return (
                        <li
                            key={s.key}
                            className="flex-1 flex flex-col items-center relative min-w-0"
                            aria-current={active ? 'step' : undefined}
                            data-testid={`shipment-step-${s.key}`}
                        >
                            {/* Row of icon + connector line. The icon
                                wrapper is a fixed-size flex box (with or
                                without a filled circle) so connectors
                                thread through the same visual centre
                                regardless of which stage is active. */}
                            <div className="flex items-center w-full">
                                {/* Left connector — hidden for step 0.
                                    Thin (0.5px) green when this segment is
                                    behind the current step; muted grey
                                    otherwise. */}
                                <div className={`flex-1 h-px ${i === 0 ? 'invisible' : (i - 1 < cur ? 'bg-emerald-400' : 'bg-slate-200')}`} />
                                <div
                                    className={`relative shrink-0 h-14 w-14 sm:h-16 sm:w-16 rounded-full flex items-center justify-center transition-all ${iconWrap}`}
                                >
                                    <s.Icon className={`${active ? 'h-9 w-9 sm:h-10 sm:w-10' : 'h-8 w-8 sm:h-9 sm:w-9'} ${iconColor}`} strokeWidth={active ? 2.25 : 1.75} />
                                    {done && (
                                        <span className="absolute -bottom-0.5 -right-0.5 h-4 w-4 rounded-full bg-emerald-500 text-white flex items-center justify-center ring-2 ring-white">
                                            <Check className="h-2.5 w-2.5" strokeWidth={3} />
                                        </span>
                                    )}
                                </div>
                                {/* Right connector — hidden for last
                                    step; thin green up to and including
                                    the segment feeding INTO the current
                                    step, muted grey after that. */}
                                <div className={`flex-1 h-px ${i === STEPS.length - 1 ? 'invisible' : (i < cur ? 'bg-emerald-400' : 'bg-slate-200')}`} />
                            </div>
                            <span className={`mt-4 text-[12px] sm:text-sm leading-tight text-center px-0.5 break-words ${label}`}>
                                {t(s.labelKey, s.fallback)}
                            </span>
                        </li>
                    );
                })}
            </ol>
        </div>
    );
};

/**
 * A single destination's tracking card. Pure — reads only from the
 * scrubbed `row` supplied by the parent. No network / no admin data.
 */
const TrackingCard = ({ row }) => {
    const { t } = useTranslation();
    const ct = row.carrier_tracking || {};
    const status = ct.normalized_status;
    const isDelivered = status === 'delivered';
    const isDelayed = (ct.delay || {}).state === 'delayed';
    const delayDays = (ct.delay || {}).days || 0;

    // ── Effective "Expected / Delivered" date ───────────────────────
    //
    // Priority (customer-facing, independent of the raw carrier/Sony
    // fields which are preserved as-is on the row):
    //
    //   1. Delivered → `ct.delivered_at` (local date)
    //   2. Out for delivery → the LOCAL date the shipment moved into
    //      OFD. We prefer ``changed_at`` (the moment normalized_status
    //      became ``out_for_delivery``); fall back to the latest-event
    //      timestamp if the hub didn't record a status-change time.
    //      This ensures a shipment that goes OFD on Oct 1 shows
    //      "Expected Oct 1", not the stale ETA (Oct 2) that may have
    //      been scheduled before dispatch.
    //   3. Carrier ETA → `ct.estimated_delivery_date`
    //   4. Current Sony planned → `ct.sony_planned_arrival_date`
    //      (and finally the row-level `planned_arrival_date` fallback).
    //
    // "Originally planned" (shown separately below) is always
    // `row.planned_arrival_date` — never shifted by this logic.
    let effectiveDate;
    if (status === 'delivered') {
        effectiveDate = localDateOf(ct.delivered_at)
            || ct.estimated_delivery_date
            || ct.sony_planned_arrival_date
            || row.planned_arrival_date;
    } else if (status === 'out_for_delivery') {
        effectiveDate = localDateOf(ct.changed_at || ct.latest_event?.timestamp)
            || ct.estimated_delivery_date
            || ct.sony_planned_arrival_date
            || row.planned_arrival_date;
    } else {
        effectiveDate = ct.estimated_delivery_date
            || ct.sony_planned_arrival_date
            || row.planned_arrival_date;
    }
    // "Originally planned" — only surface when the effective date has
    // actually moved from the Sony planned date.
    const originallyPlanned = row.planned_arrival_date;
    const showOriginal = originallyPlanned && effectiveDate && originallyPlanned !== effectiveDate;

    // Latest update line — prefer the explicit description; fall back
    // to a friendly synthesised string so the card never looks empty.
    const latestEvent = ct.latest_event || {};
    const latestText = ct.carrier_status_description || latestEvent.description;
    const latestTs = latestEvent.timestamp || ct.checked_at;

    // Current-stage label — mirrors the STEPS map above so the
    // "Current status · X" line always agrees with the highlighted
    // milestone. Falls back to the raw status string on the (very
    // rare) chance a new server status arrives before the UI knows
    // about it.
    const currentStep = STEPS.find((s) => s.key === status);
    const currentLabel = currentStep
        ? t(currentStep.labelKey, currentStep.fallback)
        : (status || '').replace(/_/g, ' ');

    return (
        <Card className="border-slate-200" data-testid={`shipment-tracking-card-${row.address_id || 'single'}`}>
            <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-2">
                        <div className={`p-2 rounded-lg ${isDelivered ? 'bg-emerald-100' : isDelayed ? 'bg-rose-100' : 'bg-indigo-100'}`}>
                            {isDelivered
                                ? <PackageCheck className={`h-4 w-4 ${isDelivered ? 'text-emerald-600' : 'text-indigo-600'}`} />
                                : <Truck className={`h-4 w-4 ${isDelayed ? 'text-rose-600' : 'text-indigo-600'}`} />}
                        </div>
                        <div>
                            <CardTitle className="text-base leading-tight">
                                {t('shipment.title', 'Shipment tracking')}
                            </CardTitle>
                            {row.label && (
                                <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1" data-testid={`shipment-destination-label-${row.address_id}`}>
                                    <MapPin className="h-3 w-3" />
                                    {row.label}
                                </p>
                            )}
                        </div>
                    </div>
                    <div className="text-right">
                        {effectiveDate && (
                            <>
                                <p className="text-[10px] uppercase tracking-wide text-slate-400 leading-tight">
                                    {isDelivered
                                        ? t('shipment.delivered_on', 'Delivered')
                                        : t('shipment.expected', 'Expected')}
                                </p>
                                <p
                                    className={`text-lg font-bold tabular-nums ${isDelivered ? 'text-emerald-700' : isDelayed ? 'text-rose-700' : 'text-slate-900'}`}
                                    data-testid={`shipment-effective-date-${row.address_id || 'single'}`}
                                >
                                    {formatDate(effectiveDate)}
                                </p>
                                {isDelayed && !isDelivered && (
                                    <p className="text-[10px] font-semibold text-rose-600 uppercase tracking-wide flex items-center gap-1 justify-end mt-0.5">
                                        <AlertTriangle className="h-3 w-3" />
                                        {t('shipment.delayed_by', 'Delayed {n}d', { n: delayDays || 1 })}
                                    </p>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </CardHeader>
            <CardContent className="pt-0">
                <MilestoneTracker status={status} />

                {/* Current status line — replaces the old repeat-the-status
                    banner. Format: "Current status · <label> · <timestamp>".
                    Split into two lines on very narrow screens so the
                    timestamp never truncates. */}
                <div
                    className="mt-4 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm"
                    data-testid={`shipment-current-status-${row.address_id || 'single'}`}
                >
                    <span className="text-slate-500">
                        {t('shipment.current_status', 'Current status')}
                    </span>
                    <span className="text-slate-400">·</span>
                    <span className={`font-semibold ${isDelivered ? 'text-emerald-700' : isDelayed ? 'text-rose-700' : 'text-indigo-700'}`}>
                        {currentLabel}
                    </span>
                    {latestTs && (
                        <>
                            <span className="text-slate-400 hidden sm:inline">·</span>
                            <span className="text-slate-500 tabular-nums w-full sm:w-auto">
                                {formatDateTime(latestTs)}
                            </span>
                        </>
                    )}
                </div>

                {latestText && (
                    <div className="mt-2 flex items-start gap-2 p-2.5 bg-slate-50 rounded border border-slate-100" data-testid={`shipment-latest-${row.address_id || 'single'}`}>
                        <div className="h-1.5 w-1.5 rounded-full bg-indigo-500 mt-1.5 flex-shrink-0" />
                        <p className="text-sm text-slate-700 leading-snug">
                            {latestText}
                        </p>
                    </div>
                )}
                {showOriginal && (
                    <p className="mt-2 text-[11px] text-slate-500">
                        {t('shipment.originally_planned', 'Originally planned {date}', { date: formatDate(originallyPlanned) })}
                    </p>
                )}
                {ct.carrier && (
                    <p className="mt-3 text-[11px] text-slate-400 flex items-center justify-end">
                        {t('shipment.carrier_prefix', 'Carrier · {name}', { name: ct.carrier })}
                    </p>
                )}
            </CardContent>
        </Card>
    );
};

const ShipmentTrackingSection = ({ order }) => {
    const rows = Array.isArray(order?.shipment_tracking) ? order.shipment_tracking : [];
    // Filter to rows that actually carry tracking. Server already
    // strips empties, but keep the guard so partial/legacy payloads
    // never render an empty card.
    const tracked = rows.filter((r) => r?.carrier_tracking?.normalized_status);
    if (tracked.length === 0) return null;

    return (
        <section className="space-y-3" data-testid="shipment-tracking-section">
            {tracked.map((row, idx) => (
                <TrackingCard key={row.address_id || `single-${idx}`} row={row} />
            ))}
        </section>
    );
};

export default ShipmentTrackingSection;
